export type PlanKey = "starter" | "challenger" | "master";

// feature.key 는 카피와 무관한 안정 식별자. INFO_CONTENTS 조회와 refundLabel 동적 치환 등 분기에 사용.
// feature.text 는 언제든 카피만 갱신 가능하도록 분리.
export interface PlanFeature {
  key: string;
  text: string;
  included: boolean;
  info?: boolean;
}

export interface PlanItem {
  key: PlanKey;
  name: string;
  price: number;
  tag?: string;
  features: PlanFeature[];
}

export const planKeyToName: Record<PlanKey, string> = {
  starter: "스타터",
  challenger: "챌린저",
  master: "마스터",
};

// pricingModel "e" / "h" / "i" / "j" / "f" 는 단일 "맞춤 플랜" 카드(challenger 매핑)를 노출하며,
// 각 모델별로 challenger 가격을 오버라이드한다.
export const SINGLE_PLAN_PRICES: Record<string, number> = {
  e: 395000,
  h: 595000,
  i: 695000,
  j: 425000,
  f: 495000,
};

export const isSinglePlanPricingModel = (model: string | undefined): boolean =>
  model !== undefined && model in SINGLE_PLAN_PRICES;

export const getSinglePlanPrice = (
  model: string | undefined
): number | undefined =>
  isSinglePlanPricingModel(model)
    ? SINGLE_PLAN_PRICES[model as string]
    : undefined;

// pricingModel "ajk" 등은 다중 플랜(S/C/M) 카드를 모두 노출하되 각 플랜 가격을 오버라이드한다.
export const MULTI_PLAN_PRICES: Record<string, Record<PlanKey, number>> = {
  aeg: { starter: 295000, challenger: 395000, master: 465000 },
  ajk: { starter: 295000, challenger: 425000, master: 625000 },
};

export const getMultiPlanPrices = (
  model: string | undefined
): Record<PlanKey, number> | undefined =>
  model !== undefined ? MULTI_PLAN_PRICES[model] : undefined;

export const applyMultiPlanPrices = (
  basePlans: PlanItem[],
  model: string | undefined
): PlanItem[] => {
  const overrides = getMultiPlanPrices(model);
  if (!overrides) return basePlans;
  return basePlans.map((p) => ({ ...p, price: overrides[p.key] ?? p.price }));
};

// 제휴 프로모션: user.promotion 으로 구분하며 S/C/M 가격을 전용가로 오버라이드.
// pricingModel 기반 오버라이드(MULTI_PLAN_PRICES)보다 우선한다.
export const PROMOTION_PLAN_PRICES: Record<string, Record<PlanKey, number>> = {
  타이탄교육그룹: { starter: 219000, challenger: 319000, master: 529000 },
  코리안매니아: { starter: 255000, challenger: 385000, master: 585000 },
};

const PROMOTION_BANNER_LABELS: Record<string, string> = {
  타이탄교육그룹: "타이탄교육그룹 제휴 혜택 적용",
  코리안매니아: "코리안매니아 제휴 혜택 적용",
};

// 제휴 프로모션 사용자 여부. 광고 추적(meta/capi/firebase 등) 제외 분기에도 사용.
export const isPromotionUser = (promotion: string | undefined): boolean =>
  promotion !== undefined && promotion in PROMOTION_PLAN_PRICES;

export const getPromotionPrices = (
  promotion: string | undefined
): Record<PlanKey, number> | undefined =>
  promotion !== undefined ? PROMOTION_PLAN_PRICES[promotion] : undefined;

export const applyPromotionPrices = (
  basePlans: PlanItem[],
  promotion: string | undefined
): PlanItem[] => {
  const overrides = getPromotionPrices(promotion);
  if (!overrides) return basePlans;
  return basePlans.map((p) => ({ ...p, price: overrides[p.key] ?? p.price }));
};

// 전용 결제창 상단 띠 라벨. 해당 없으면 undefined → 띠 미노출.
export const getDedicatedPaymentBannerLabel = (
  promotion: string | undefined
): string | undefined =>
  promotion !== undefined ? PROMOTION_BANNER_LABELS[promotion] : undefined;

// 파트너 결제 단독 페이지 슬러그 → 기존 promotion 키 매핑.
// 예: /partner/titan-education-group/payment → "타이탄교육그룹" (가격/배너 재사용)
// 신규 파트너 추가 시 PROMOTION_PLAN_PRICES/PROMOTION_BANNER_LABELS 와 함께 여기 등록.
const PARTNER_SLUG_TO_PROMOTION: Record<string, string> = {
  "titan-education-group": "타이탄교육그룹",
};

export const getPromotionBySlug = (
  slug: string | undefined
): string | undefined => (slug ? PARTNER_SLUG_TO_PROMOTION[slug] : undefined);

// paymentPageType === "p" : 마스터 플랜 features 최상단에 "수석 코치 배정" 행 노출.
// INFO_CONTENTS 의 "lead_coach" key 와 매핑.
export const PAYMENT_PAGE_TYPE_P_MASTER_FEATURE: PlanFeature = {
  key: "lead_coach",
  text: "수석 코치 배정",
  included: true,
  info: true,
};

export const applyPaymentPageType = (
  basePlans: PlanItem[],
  paymentPageType: string | undefined
): PlanItem[] => {
  if (paymentPageType !== "p") return basePlans;
  return basePlans.map(
    (plan: PlanItem): PlanItem =>
      plan.key === "master"
        ? {
            ...plan,
            features: [PAYMENT_PAGE_TYPE_P_MASTER_FEATURE, ...plan.features],
          }
        : plan
  );
};

// q 결제 페이지 마스터 플랜 features 최상단 라벨: 수석 코치 배정 / 코칭 핏 보장 / 플렉서블 홀딩.
export const PAYMENT_PAGE_TYPE_Q_MASTER_FEATURES: PlanFeature[] = [
  { key: "lead_coach", text: "수석 코치 배정", included: true, info: true },
  { key: "coaching_fit", text: "코칭 핏 보장", included: true, info: true },
  {
    key: "flexible_holding",
    text: "플렉서블 홀딩",
    included: true,
    info: true,
  },
];

// 결제 페이지 마스터 플랜 features 가공.
// - "수석 코치 배정"(lead_coach)은 paymentPageType 과 무관하게 항상 노출(이미 있으면 중복 추가 안 함).
// - q 는 그 아래에 "코칭 핏 보장 / 플렉서블 홀딩"을 함께 노출.
export const applyQMasterFeatures = (
  basePlans: PlanItem[],
  paymentPageType: string | undefined
): PlanItem[] => {
  return basePlans.map((plan: PlanItem): PlanItem => {
    if (plan.key !== "master") return plan;
    if (paymentPageType === "q") {
      return {
        ...plan,
        tag: "가장 인기 있는",
        features: [...PAYMENT_PAGE_TYPE_Q_MASTER_FEATURES, ...plan.features],
      };
    }
    const hasLeadCoach = plan.features.some((f) => f.key === "lead_coach");
    return hasLeadCoach
      ? plan
      : {
          ...plan,
          features: [PAYMENT_PAGE_TYPE_P_MASTER_FEATURE, ...plan.features],
        };
  });
};
